import { z } from 'zod'

/**
 * Auth validation schemas.
 * Used by React Hook Form + Zod resolver for type-safe form validation.
 */

// ─── Login ──────────────────────────────────────

export const loginSchema = z.object({
	email: z.string().min(1, 'El correo es requerido').email('Formato de correo inválido'),
	password: z
		.string()
		.min(1, 'La contraseña es requerida')
		.min(8, 'La contraseña debe tener al menos 8 caracteres'),
	keepSession: z.boolean().default(false),
})

export type LoginFormData = z.infer<typeof loginSchema>

// ─── Register ───────────────────────────────────

export const registerSchema = z
	.object({
		email: z.string().min(1, 'El correo es requerido').email('Formato de correo inválido'),
		password: z
			.string()
			.min(8, 'La contraseña debe tener al menos 8 caracteres')
			.regex(/[A-Z]/, 'Debe contener al menos una mayúscula')
			.regex(/[a-z]/, 'Debe contener al menos una minúscula')
			.regex(/[0-9]/, 'Debe contener al menos un número'),
		confirmPassword: z.string().min(1, 'Confirma tu contraseña'),
		firstName: z.string().optional(),
		lastName: z.string().optional(),
		acceptTerms: z.boolean().refine((val) => val === true, {
			message: 'Debes aceptar los términos',
		}),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: 'Las contraseñas no coinciden',
		path: ['confirmPassword'],
	})

export type RegisterFormData = z.infer<typeof registerSchema>

// ─── Reset Password ─────────────────────────────

export const resetPasswordSchema = z.object({
	email: z.string().min(1, 'El correo es requerido').email('Formato de correo inválido'),
})

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

// ─── Update Password ────────────────────────────

export const updatePasswordSchema = z
	.object({
		password: z
			.string()
			.min(8, 'La contraseña debe tener al menos 8 caracteres')
			.regex(/[A-Z]/, 'Debe contener al menos una mayúscula')
			.regex(/[a-z]/, 'Debe contener al menos una minúscula')
			.regex(/[0-9]/, 'Debe contener al menos un número'),
		confirmPassword: z.string().min(1, 'Confirma tu contraseña'),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: 'Las contraseñas no coinciden',
		path: ['confirmPassword'],
	})

export type UpdatePasswordFormData = z.infer<typeof updatePasswordSchema>

// ─── Registration Wizard ────────────────────────

export const step0Schema = z
	.object({
		email: z.string().min(1, 'El correo es requerido').email('Formato de correo inválido'),
		password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
		confirmPassword: z.string().min(1, 'Confirma tu contraseña'),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: 'Las contraseñas no coinciden',
		path: ['confirmPassword'],
	})

export type Step0FormData = z.infer<typeof step0Schema>

export const step1Schema = z.object({
	companyName: z.string().min(1, 'El nombre de la empresa es requerido'),
	documentType: z.string().min(1, 'Selecciona el tipo de documento'),
	employeeCount: z.string().min(1, 'Selecciona el número de empleados'),
	position: z.string().min(1, 'Selecciona tu cargo'),
	country: z.string().min(1, 'Selecciona el país'),
	operatingTime: z.string().min(1, 'Selecciona el tiempo de operación'),
	revenueLevel: z.string().min(1, 'Selecciona el nivel de facturación'),
	businessSegment: z.string().min(1, 'Selecciona el segmento de negocio'),
})

export type Step1FormData = z.infer<typeof step1Schema>

export const step2Schema = z.object({
	isProfitable: z.string().min(1, 'Requerido'),
	hasClearKPIs: z.string().min(1, 'Requerido'),
	knowsCAC: z.string().min(1, 'Requerido'),
	salesChannel: z.string().min(1, 'Requerido'),
	productType: z.string().min(1, 'Requerido'),
	salesModel: z.string().min(1, 'Requerido'),
	grossMargin: z.string().min(1, 'Requerido'),
	hasDebt: z.string().min(1, 'Requerido'),
})

export type Step2FormData = z.infer<typeof step2Schema>

export const step3Schema = z.object({
	website: z.string().url('URL inválida').or(z.literal('')).optional(),
	instagram: z.string().optional(),
	facebook: z.string().optional(),
	tiktok: z.string().optional(),
	linkedin: z.string().optional(),
	otherChannel: z.string().optional(),
})

export type Step3FormData = z.infer<typeof step3Schema>
